import type { StateCreator } from 'zustand';
import type { StoreState } from './index';
import type { SEOBatchProgress, SEOBatchComplete, SEOData, LighthouseScores } from '../types';
import { outreachApi } from '../services/outreachApi';
import type {
  TargetPlatform, IGEnrichmentSnapshot, WebsiteEnrichmentSnapshot, MapsCompetitor,
  OutreachTarget, PitchConfig, DefaultMessage, HookOption, MessageVariant, MessageTemplate,
  OutreachPoolText, OutreachSentMessage, FunnelStats,
} from '../services/outreachApi';

// Re-export the outreach API types so existing consumers (e.g. BusinessOutreach.tsx) keep
// importing them from the slice. The shapes now live in services/outreachApi.ts.
export type {
  TargetPlatform, IGEnrichmentSnapshot, WebsiteEnrichmentSnapshot, MapsCompetitor,
  OutreachTarget, OutreachLayer, OutreachTag, PitchTemplateSection, PitchTemplate,
  PitchConfig, SectionSnippet, DefaultMessage, HookOption, MessageVariant, MessageTemplate,
  OutreachPoolText, OutreachSentMessage, FunnelStats,
} from '../services/outreachApi';

// ---------------------------------------------------------------------------
// Übergabe-Paket aus fraud0: überlebt ein Neuladen
//
// Der Store wird nicht persistiert, und die Zieladresse trägt nur ?prefill=1 — ohne Zwischenlager
// war das Paket nach einem Neuladen der Outreach-Seite weg und der Dialog ging gar nicht erst auf.
// Sitzungsspeicher statt lokalem Speicher: das Paket enthält Kontaktdaten und soll mit dem Tab
// enden, nicht auf dem Rechner liegen bleiben.
// ---------------------------------------------------------------------------

const PREFILL_KEY = 'fraud0:outreach-prefill';

export function rememberPrefill(p: unknown): void {
  try {
    if (p) sessionStorage.setItem(PREFILL_KEY, JSON.stringify(p));
    else sessionStorage.removeItem(PREFILL_KEY);
  } catch { /* privater Modus / voller Speicher: dann eben nur im Arbeitsspeicher */ }
}

export function readRememberedPrefill(): OutreachSlice['outreachPendingPrefill'] {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface OutreachSlice {
  outreachTargets: OutreachTarget[];
  outreachActiveTarget: OutreachTarget | null;
  outreachActiveContact: OutreachTarget | null;
  outreachActivePlatform: string | null;
  outreachBrowsePhase: string;
  /** Der angesehene Beziehungszustand. Die alte Phase bleibt daneben stehen — LibraryPanel, der
   *  Erzeugen-Knopf und die Extension sprechen weiter Phase. */
  outreachBrowseState: string;
  outreachBrowseSubState: string | null;
  outreachStateCounts: { states: Record<string, number>; sub_states: Record<string, number>; unassigned: number };
  /** Konnektoren passen in JEDEN Zustand und liegen darum neben der Liste, nicht darin. */
  outreachConnectors: OutreachPoolText[];
  outreachPoolTexts: OutreachPoolText[];
  outreachExperimentalTexts: OutreachPoolText[];
  /** Quelltexte ohne Pool in der angesehenen Kombination — Grundlage fuer den Erzeugen-Knopf. */
  outreachMissingSources: number;
  outreachPendingReviews: OutreachSentMessage[];
  outreachPendingCount: number;
  outreachFunnel: FunnelStats;
  /** Dieselben Ziele wie im Trichter, nur nach Zustand gezaehlt. `null` = noch nicht geladen. */
  outreachStateStats: {
    states: Record<string, number>;
    markers: Record<string, number>;
    total: number;
  } | null;
  outreachDeranked: OutreachPoolText[];
  outreachFilters: { platform: string | null; industry: string | null };
  outreachLoading: boolean;
  outreachBrowseLoading: boolean;
  outreachCopyLoading: boolean;
  outreachLastCopied: { personalized_text: string; pool_text_id: number; phase?: string; platform?: string } | null;
  outreachIGHistory: IGEnrichmentSnapshot[];
  outreachWebsiteHistory: WebsiteEnrichmentSnapshot[];
  outreachMapsCompetitors: MapsCompetitor[];
  outreachEnrichmentLoading: boolean;

  fetchOutreachTargets: (platform?: string, industry?: string) => Promise<void>;
  createOutreachTarget: (data: Partial<OutreachTarget> & { platforms?: string[] }) => Promise<OutreachTarget>;
  updateOutreachTarget: (id: number, data: Partial<OutreachTarget>) => Promise<void>;
  archiveOutreachTarget: (id: number) => Promise<void>;
  changeOutreachPhase: (id: number, phase: string, confirmSkip?: boolean, platform?: string) => Promise<OutreachTarget>;
  setOutreachActiveTarget: (target: OutreachTarget | null) => void;
  setOutreachActiveContact: (contact: OutreachTarget | null) => void;
  setOutreachActivePlatform: (platform: string | null) => void;

  createOutreachContact: (parentId: number, data: Partial<OutreachTarget>) => Promise<void>;
  archiveOutreachContact: (contactId: number) => Promise<void>;
  setOutreachBrowsePhase: (phase: string) => void;
  setOutreachBrowseState: (state: string, subState?: string | null) => void;
  /** Zustand von Hand setzen. Gibt die Warnung zurueck, wenn der Server nachfragt (409) —
   *  NICHT werfen: der alte Phasen-Pfad hatte kein `catch` und verschluckte genau das. */
  setTargetState: (targetId: number, platform: string, state: string | null,
                   opts?: { subState?: string | null; confirmed?: boolean })
    => Promise<{ ok: true } | { ok: false; warning: string }>;
  /** Merker setzen oder mit `null` wegnehmen. Eigene Achse: ruehrt Zustand und Phase nicht an,
   *  darum auch kein Warnungs-Rueckgabewert wie bei `setTargetState`. */
  setTargetMarker: (targetId: number, platform: string, marker: string | null) => Promise<void>;
  setOutreachFilters: (filters: { platform?: string | null; industry?: string | null }) => void;

  addTargetPlatform: (targetId: number, platform: string, style?: string) => Promise<TargetPlatform>;
  removeTargetPlatform: (targetId: number, platform: string) => Promise<void>;
  updateTargetPlatform: (targetId: number, platform: string, data: Partial<TargetPlatform>) => Promise<void>;

  browseOutreachTexts: (phase: string, platform: string, language: string, generate?: boolean) => Promise<void>;
  browseOutreachState: (state: string, platform: string, language: string, subState?: string | null) => Promise<void>;
  loadOutreachStateCounts: (platform: string, language: string) => Promise<void>;
  browseOutreachExperimental: (phase: string, platform: string, language: string) => Promise<void>;
  // `phase` = die ANGESEHENE Phase. Seit die Phasen-Chips nur noch bloettern, kann sie von der
  // Pipeline-Stufe abweichen; ohne den Parameter personalisierte der Server mit der falschen
  // Intensitaet und verbuchte den Versand unter der falschen Phase.
  copyOutreachText: (pool_text_id: number, target_id: number, platform?: string, deep_personalize?: boolean, outreach_style?: 'direkt' | 'subtil', phase?: string) => Promise<{ personalized_text: string; pool_text_id: number; phase: string; platform: string }>;
  markOutreachSent: (args: { pool_text_id?: number; library_text_id?: number; library_root_id?: number;
    target_id: number; platform?: string; phase?: string; personalized_text?: string }) => Promise<{ sent_message_id: number }>;
  getTargetVariables: (targetId: number) => Promise<{ token: string; label: string; value: string }[]>;
  submitOutreachReview: (sent_message_id: number, rating: string, their_response?: string, actual_sent_text?: string) => Promise<void>;
  undoOutreachSent: (sent_message_id: number) => Promise<void>;

  analyzeIGScreenshot: (targetId: number, screenshotBase64: string) => Promise<IGEnrichmentSnapshot>;
  fetchIGEnrichmentHistory: (targetId: number) => Promise<void>;
  enrichWebsite: (targetId: number, method: string, data: Record<string, string>) => Promise<void>;
  fetchWebsiteHistory: (targetId: number) => Promise<void>;
  searchMapsCompetitors: (targetId: number, query: string) => Promise<void>;

  fetchOutreachPendingReviews: () => Promise<void>;
  fetchOutreachStats: () => Promise<void>;
  fetchOutreachDeranked: () => Promise<void>;
  fetchSimilarTargets: (industry?: string, competitor?: string) => Promise<OutreachTarget[]>;

  // SEO Intelligence state
  outreachSEOBatchRunning: boolean
  outreachSEOBatchProgress: SEOBatchProgress | null
  outreachSEOBatchResult: SEOBatchComplete | null

  // SEO Intelligence actions
  triggerSEOBatch: (targetIds?: number[]) => Promise<void>
  analyzeSEOSingle: (targetId: number) => Promise<SEOData>
  analyzeLighthouse: (targetId: number) => Promise<LighthouseScores | null>
  resolveBioLink: (targetId: number, url: string) => Promise<{ resolved_domain: string | null; all_links: string[]; method: string }>
  fetchSEOData: (targetId: number) => Promise<SEOData>
  handleSEOBatchSSE: (event: Record<string, unknown>) => void

  // Fraud0-lead handoff prefill (set on the leads page, consumed on the outreach page)
  outreachPendingPrefill: (Partial<OutreachTarget> & { platforms?: string[]; linkedin_gf_url?: string | null; marketing_position?: string | null; pain_point_options?: string[] }) | null
  setOutreachPendingPrefill: (p: OutreachSlice['outreachPendingPrefill']) => void

  // Fraud0-pitch message generator
  getPitchConfig: (strategyId?: number | null) => Promise<PitchConfig>
  getDefaultMessage: (targetId: number) => Promise<DefaultMessage>
  savePitchConfig: (body: { kind: 'layer' | 'tag' | 'default' | 'template_section'; key: string; prompt_text?: string; enabled?: boolean; size?: string; strategy_id?: number | null }) => Promise<PitchConfig>
  suggestHooks: (targetId: number, tag: string | null, anrede: string, forceNew?: boolean) => Promise<{ options: HookOption[]; from_cache: boolean; tag: string | null }>
  rewriteHook: (targetId: number, original: string, tag: string | null, instruction?: string, anrede?: string) => Promise<string>
  generatePitchMessage: (targetId: number, body: { tags: (string | null)[]; anrede: string; cta_style?: string; length_chars?: number; hooks?: Record<string, string> }) => Promise<MessageVariant[]>
  rewriteSection: (targetId: number, body: { section_key: string; sections: Record<string, string>; tag: string | null; instruction?: string; anrede?: string }) => Promise<{ section_key: string; content: string; warnings: string[] }>
  listMessageTemplates: () => Promise<MessageTemplate[]>
  saveMessageTemplate: (body: { name: string; sections: Record<string, string>; tag?: string; anrede?: string; sizes?: Record<string, string> }) => Promise<number>
}

// ---------------------------------------------------------------------------
// Slice implementation
// ---------------------------------------------------------------------------

export const createOutreachSlice: StateCreator<StoreState, [], [], OutreachSlice> = (set, get) => ({
  outreachTargets: [],
  outreachActiveTarget: null,
  outreachActiveContact: null,
  outreachActivePlatform: null,
  outreachBrowsePhase: 'kalt',
  outreachBrowseState: 'nicht_verbunden',
  outreachBrowseSubState: null,
  outreachStateCounts: { states: {}, sub_states: {}, unassigned: 0 },
  outreachConnectors: [],
  outreachPoolTexts: [],
  outreachExperimentalTexts: [],
  outreachMissingSources: 0,
  outreachPendingReviews: [],
  outreachPendingCount: 0,
  outreachFunnel: { kalt: 0, interesse: 0, vertiefung: 0, closing: 0 },
  outreachStateStats: null,
  outreachDeranked: [],
  outreachFilters: { platform: null, industry: null },
  outreachLoading: false,
  outreachBrowseLoading: false,
  outreachCopyLoading: false,
  outreachLastCopied: null,
  outreachIGHistory: [],
  outreachWebsiteHistory: [],
  outreachMapsCompetitors: [],
  outreachEnrichmentLoading: false,
  outreachSEOBatchRunning: false,
  outreachSEOBatchProgress: null,
  outreachSEOBatchResult: null,

  fetchOutreachTargets: async (platform, industry) => {
    set({ outreachLoading: true });
    try {
      const targets = await outreachApi.listTargets(platform, industry);
      set({ outreachTargets: targets, outreachLoading: false });
    } catch {
      set({ outreachLoading: false });
    }
  },

  createOutreachTarget: async (data) => {
    const target = await outreachApi.createTarget(data);
    set((s) => ({ outreachTargets: [target, ...s.outreachTargets] }));
    return target;
  },

  // Beim Start aus dem Sitzungsspeicher holen: die Übergabe wechselt die Seite, und ein Neuladen
  // dort hätte das Paket sonst verloren (die Adresse trägt nur das Kennzeichen ?prefill=1, keine
  // Daten) — der Dialog wäre leer aufgegangen oder gar nicht.
  outreachPendingPrefill: readRememberedPrefill(),
  setOutreachPendingPrefill: (p) => { rememberPrefill(p); return set({ outreachPendingPrefill: p }); },

  getPitchConfig: async (strategyId) => outreachApi.getPitchConfig(strategyId),
  getDefaultMessage: async (targetId) => outreachApi.defaultMessage(targetId),
  savePitchConfig: async (body) => outreachApi.savePitchConfig(body),
  suggestHooks: async (targetId, tag, anrede, forceNew = false) =>
    outreachApi.suggestHooks(targetId, tag, anrede, forceNew),
  rewriteHook: async (targetId, original, tag, instruction, anrede = 'sie') => {
    const r = await outreachApi.rewriteHook(targetId, original, tag, instruction, anrede);
    return r.filled_text;
  },
  generatePitchMessage: async (targetId, body) => {
    const r = await outreachApi.generateMessage(targetId, body);
    return r.variants;
  },
  rewriteSection: async (targetId, body) => outreachApi.rewriteSection(targetId, body),
  listMessageTemplates: async () => {
    const r = await outreachApi.listTemplates();
    return r.items;
  },
  saveMessageTemplate: async (body) => {
    const r = await outreachApi.saveTemplate(body);
    return r.id;
  },

  updateOutreachTarget: async (id, data) => {
    const updated = await outreachApi.updateTarget(id, data);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === id ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === id ? updated : s.outreachActiveTarget,
    }));
  },

  archiveOutreachTarget: async (id) => {
    await outreachApi.archiveTarget(id);
    set((s) => ({
      outreachTargets: s.outreachTargets.filter((t) => t.id !== id),
      outreachActiveTarget: s.outreachActiveTarget?.id === id ? null : s.outreachActiveTarget,
    }));
  },

  /**
   * TOTER PFAD, seit S4 von keiner Ansicht mehr benutzt — Zustaende setzt `setTargetState`.
   *
   * ⚠️ Zwei Fallen, warum man ihn nicht wieder anschliessen sollte: er hat kein `catch`, der 409
   * des Servers geht also unbemerkt verloren, und die Aufrufer uebergaben `confirm_skip` hart als
   * `true`, womit die Rueckfrage praktisch aus war.
   *
   * Die fruehere Begruendung „bleibt stehen, weil die Extension Phase spricht" war falsch und ist
   * in S6 nachgemessen: die Extension LIEST die Phase (Abzeichen, Filter, `/browse/{phase}`),
   * schreibt sie aber nie — `PUT /targets/{id}/phase` hat im ganzen Repo **keinen** Aufrufer mehr.
   * Die Kette Slice → `outreachApi.changePhase` → Endpunkt haengt komplett in der Luft. Sie bleibt
   * trotzdem stehen, aber als bewusster Rollback-Punkt, nicht wegen eines Nutzers, den es gibt.
   * Siehe `past-plans/s6-aufraeumbericht.md`.
   */
  changeOutreachPhase: async (id, phase, confirmSkip = false, platform) => {
    const body: { phase: string; confirm_skip: boolean; platform?: string } = { phase, confirm_skip: confirmSkip };
    if (platform) body.platform = platform;
    const updated = await outreachApi.changePhase(id, body);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === id ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === id ? updated : s.outreachActiveTarget,
    }));
    return updated;
  },

  setOutreachActiveTarget: (target) => {
    const firstPlatform = target?.platforms?.[0]?.platform || target?.platform || null;
    set({ outreachActiveTarget: target, outreachActivePlatform: firstPlatform });
  },

  setOutreachActiveContact: (contact) => set({ outreachActiveContact: contact }),

  setOutreachActivePlatform: (platform) => set({ outreachActivePlatform: platform }),

  createOutreachContact: async (parentId, data) => {
    await outreachApi.createTarget({ ...data, parent_id: parentId, entry_type: 'contact' });
    // Refresh the active target to update contacts list
    const updated = await outreachApi.getTarget(parentId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === parentId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === parentId ? updated : s.outreachActiveTarget,
    }));
  },

  archiveOutreachContact: async (contactId) => {
    await outreachApi.archiveTarget(contactId);
    // Refresh the active target to update contacts list
    const activeTarget = get().outreachActiveTarget;
    if (activeTarget) {
      const updated = await outreachApi.getTarget(activeTarget.id);
      set((s) => ({
        outreachTargets: s.outreachTargets.map((t) => (t.id === activeTarget.id ? updated : t)),
        outreachActiveTarget: s.outreachActiveTarget?.id === activeTarget.id ? updated : s.outreachActiveTarget,
        outreachActiveContact: s.outreachActiveContact?.id === contactId ? null : s.outreachActiveContact,
      }));
    }
  },

  setOutreachBrowsePhase: (phase) => set({ outreachBrowsePhase: phase }),
  setOutreachBrowseState: (state, subState = null) =>
    set({ outreachBrowseState: state, outreachBrowseSubState: subState }),

  setTargetState: async (targetId, platform, state, opts = {}) => {
    try {
      const updated = await outreachApi.setState(targetId, {
        state, platform, sub_state: opts.subState ?? null, confirmed: opts.confirmed ?? false,
      });
      // Ziel-Liste UND aktive Auswahl nachziehen, sonst zeigt der Chip den alten Zustand, bis
      // jemand die Seite neu laedt.
      set((s) => ({
        outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
        outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
        outreachActiveContact: s.outreachActiveContact?.id === targetId
          ? (updated.contacts?.find((c) => c.id === targetId) ?? updated)
          : s.outreachActiveContact,
      }));
      return { ok: true as const };
    } catch (e) {
      // Der 409 traegt die Begruendung im FastAPI-Body. Ihn zu verschlucken hiesse: der Klick
      // tut nichts, und niemand erfaehrt warum — genau der Altfehler aus changeOutreachPhase.
      const detail = (e as { detail?: { requires_confirmation?: boolean; message?: string } })?.detail;
      if (detail?.requires_confirmation) return { ok: false as const, warning: detail.message ?? 'Wirklich?' };
      throw e;
    }
  },

  setTargetMarker: async (targetId, platform, marker) => {
    const updated = await outreachApi.setMarker(targetId, { marker, platform });
    // Dieselbe Nachzieh-Regel wie bei `setTargetState` — sonst zeigt die Zeile den alten Merker,
    // bis jemand die Seite neu laedt.
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
      outreachActiveContact: s.outreachActiveContact?.id === targetId
        ? (updated.contacts?.find((c) => c.id === targetId) ?? updated)
        : s.outreachActiveContact,
    }));
  },

  setOutreachFilters: (filters) =>
    set((s) => ({ outreachFilters: { ...s.outreachFilters, ...filters } })),

  addTargetPlatform: async (targetId, platform, style) => {
    const result = await outreachApi.addPlatform(targetId, platform, style);
    // Refresh target
    const updated = await outreachApi.getTarget(targetId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
    }));
    return result;
  },

  removeTargetPlatform: async (targetId, platform) => {
    await outreachApi.removePlatform(targetId, platform);
    const updated = await outreachApi.getTarget(targetId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
    }));
  },

  updateTargetPlatform: async (targetId, platform, data) => {
    await outreachApi.updatePlatform(targetId, platform, data);
    const updated = await outreachApi.getTarget(targetId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
    }));
  },

  browseOutreachTexts: async (phase, platform, language, generate = false) => {
    set({ outreachBrowseLoading: true });
    try {
      const result = await outreachApi.browse(phase, platform, language, generate);
      set({
        outreachPoolTexts: result.texts,
        outreachMissingSources: result.missing_sources ?? 0,
        outreachBrowseLoading: false,
      });
    } catch {
      set({ outreachBrowseLoading: false });
    }
  },

  browseOutreachState: async (state, platform, language, subState = null) => {
    set({ outreachBrowseLoading: true });
    try {
      const result = await outreachApi.browseState(state, platform, language, subState ?? undefined);
      set({
        outreachPoolTexts: result.texts,
        outreachConnectors: result.connectors ?? [],
        // Die alte Phase kommt VOM SERVER mit, damit es keine zweite Zuordnungstabelle im
        // Browser gibt. Sie haelt LibraryPanel, Erzeugen-Knopf und Experimental-Tab am Laufen.
        outreachBrowsePhase: result.phase || 'kalt',
        // Auf dieser Route wird nichts nachgeneriert, also gibt es auch nichts zu melden.
        outreachMissingSources: 0,
        outreachBrowseLoading: false,
      });
    } catch {
      set({ outreachBrowseLoading: false });
    }
  },

  loadOutreachStateCounts: async (platform, language) => {
    try {
      const r = await outreachApi.stateCounts(platform, language);
      set({ outreachStateCounts: { states: r.states, sub_states: r.sub_states, unassigned: r.unassigned } });
    } catch {
      // Zahlen sind Beiwerk — bleibt der Aufruf aus, zeigen die Chips keine Zahl statt gar nichts.
    }
  },

  browseOutreachExperimental: async (phase, platform, language) => {
    try {
      const result = await outreachApi.browseExperimental(phase, platform, language);
      set({ outreachExperimentalTexts: result.texts });
    } catch {
      // ignore
    }
  },

  copyOutreachText: async (pool_text_id, target_id, platform, deep_personalize, outreach_style, phase) => {
    set({ outreachCopyLoading: true });
    try {
      const body: Record<string, unknown> = { pool_text_id, target_id };
      if (platform) body.platform = platform;
      if (deep_personalize) body.deep_personalize = deep_personalize;
      if (outreach_style) body.outreach_style = outreach_style;
      if (phase) body.phase = phase;
      const result = await outreachApi.copy(body);
      set({ outreachLastCopied: result, outreachCopyLoading: false });
      return result;
    } catch (e) {
      set({ outreachCopyLoading: false });
      throw e;
    }
  },

  markOutreachSent: async ({ pool_text_id, library_text_id, library_root_id, target_id, platform, phase, personalized_text }) => {
    return outreachApi.markSent({ pool_text_id, library_text_id, library_root_id, target_id, platform, phase, personalized_text });
  },

  getTargetVariables: async (targetId) => {
    const r = await outreachApi.getVariables(targetId);
    return r.variables ?? [];
  },

  submitOutreachReview: async (sent_message_id, rating, their_response, actual_sent_text) => {
    await outreachApi.submitReview({ sent_message_id, rating, their_response, actual_sent_text });
    set((s) => ({
      outreachPendingReviews: s.outreachPendingReviews.filter((r) => r.id !== sent_message_id),
      outreachPendingCount: Math.max(0, s.outreachPendingCount - 1),
    }));
  },

  undoOutreachSent: async (sent_message_id) => {
    await outreachApi.undoSent(sent_message_id);
    set((s) => ({
      outreachPendingReviews: s.outreachPendingReviews.filter((r) => r.id !== sent_message_id),
      outreachPendingCount: Math.max(0, s.outreachPendingCount - 1),
    }));
  },

  analyzeIGScreenshot: async (targetId, screenshotBase64) => {
    set({ outreachEnrichmentLoading: true });
    try {
      const result = await outreachApi.analyzeIG(targetId, screenshotBase64);
      // Refresh target + history
      const updated = await outreachApi.getTarget(targetId);
      set((s) => ({
        outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
        outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
        outreachEnrichmentLoading: false,
      }));
      return result.analysis;
    } catch (e) {
      set({ outreachEnrichmentLoading: false });
      throw e;
    }
  },

  fetchIGEnrichmentHistory: async (targetId) => {
    const result = await outreachApi.igHistory(targetId);
    set({ outreachIGHistory: result.snapshots });
  },

  enrichWebsite: async (targetId, method, data) => {
    set({ outreachEnrichmentLoading: true });
    try {
      await outreachApi.enrichWebsite(targetId, { method, ...data });
      const updated = await outreachApi.getTarget(targetId);
      set((s) => ({
        outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
        outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
        outreachEnrichmentLoading: false,
      }));
    } catch (e) {
      set({ outreachEnrichmentLoading: false });
      throw e;
    }
  },

  fetchWebsiteHistory: async (targetId) => {
    const result = await outreachApi.websiteHistory(targetId);
    set({ outreachWebsiteHistory: result.snapshots });
  },

  searchMapsCompetitors: async (targetId, query) => {
    set({ outreachEnrichmentLoading: true });
    try {
      const result = await outreachApi.mapsAnalysis(targetId, query);
      const updated = await outreachApi.getTarget(targetId);
      set((s) => ({
        outreachMapsCompetitors: result.competitors,
        outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
        outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
        outreachEnrichmentLoading: false,
      }));
    } catch (e) {
      set({ outreachEnrichmentLoading: false });
      throw e;
    }
  },

  fetchOutreachPendingReviews: async () => {
    const result = await outreachApi.pendingReviews();
    set({ outreachPendingReviews: result.reviews, outreachPendingCount: result.count });
  },

  fetchOutreachStats: async () => {
    const result = await outreachApi.stats();
    set({
      outreachFunnel: result.funnel,
      outreachPendingCount: result.pending_reviews,
      outreachStateStats: {
        states: result.states, markers: result.markers, total: result.total,
      },
    });
  },

  fetchOutreachDeranked: async () => {
    const texts = await outreachApi.deranked();
    set({ outreachDeranked: texts });
  },

  fetchSimilarTargets: async (industry, competitor) => outreachApi.similarTargets(industry, competitor),

  triggerSEOBatch: async (targetIds) => {
    set({ outreachSEOBatchRunning: true, outreachSEOBatchProgress: null });
    try {
      const body = targetIds ? { target_ids: targetIds } : { all_unenriched: true };
      await outreachApi.seoBatch(body);
    } catch (e) {
      set({ outreachSEOBatchRunning: false });
      throw e;
    }
    // outreachSEOBatchRunning set to false by seo_batch_complete SSE event
  },

  analyzeSEOSingle: async (targetId) => {
    const result = await outreachApi.seoSingle(targetId);
    // Refresh the target in store
    const updated = await outreachApi.getTarget(targetId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
    }));
    return result;
  },

  analyzeLighthouse: async (targetId) => outreachApi.lighthouse(targetId),

  resolveBioLink: async (targetId, url) => {
    const result = await outreachApi.resolveBioLink(targetId, url);
    const updated = await outreachApi.getTarget(targetId);
    set((s) => ({
      outreachTargets: s.outreachTargets.map((t) => (t.id === targetId ? updated : t)),
      outreachActiveTarget: s.outreachActiveTarget?.id === targetId ? updated : s.outreachActiveTarget,
    }));
    return result;
  },

  fetchSEOData: async (targetId) => {
    return outreachApi.seoData(targetId);
  },

  handleSEOBatchSSE: (event: Record<string, unknown>) => {
    if (event.event === 'seo_batch_progress') {
      set({
        outreachSEOBatchProgress: {
          target_id: event.target_id as number,
          done: event.done as number,
          total: event.total as number,
          status: event.status as 'ok' | 'no_data' | 'error',
        },
      });
    } else if (event.event === 'seo_batch_complete') {
      set({
        outreachSEOBatchRunning: false,
        outreachSEOBatchResult: {
          total: event.total as number,
          success: event.success as number,
          no_data: event.no_data as number,
          errors: event.errors as number,
          total_cost_usd: event.total_cost_usd as number,
        },
      });
    }
  },
});
